import { FC, ReactNode, useEffect, useState } from 'react'
import { Box, Switch, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Capacitor } from '@capacitor/core'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import { useUserConsents } from 'services/profile/profile.hooks'
import { useAccessToken } from 'services/token/token.hooks'
import {
  initPushNotifications,
  isPushNotificationsEnabled,
  unregisterPushNotifications
} from 'services/pushNotifications'
import theme from 'styles/theme'

interface ConsentRowProps {
  title: string
  subtitle?: string
  checked: boolean
  disabled?: boolean
  first?: boolean
  onToggle: () => void
}

const ConsentRow: FC<ConsentRowProps> = ({ title, subtitle, checked, disabled, first, onToggle }) => (
  <Box
    onClick={disabled ? undefined : onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px',
      backgroundColor: '#fff',
      px: '24px',
      py: subtitle ? '14px' : '17px',
      borderTop: first ? `1px solid ${theme.r4r.greyLight}` : 'none',
      borderBottom: `1px solid ${theme.r4r.greyLight}`,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      ':hover': { backgroundColor: disabled ? '#fff' : 'rgba(31, 31, 115, 0.04)' }
    }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '15px', fontWeight: 600, color: theme.r4r.ink, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {!!subtitle && (
        <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Switch checked={checked} disabled={disabled} />
  </Box>
)

const SectionLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '11px',
      fontWeight: 600,
      color: theme.r4r.grey,
      letterSpacing: '0.3px',
      textTransform: 'uppercase',
      mb: '6px'
    }}>
    {children}
  </Typography>
)

const UserDetailsConsents: FC = () => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { enqueueSnackbar } = useSnackbar()
  const { accessToken } = useAccessToken()
  const {
    usersProfileMarketing = {
      marketingConsent: false,
      newsletterConsent: false
    },
    usersProfileMarketingUpdate
  } = useUserConsents()

  const isNative = Capacitor.isNativePlatform()
  const [pushEnabled, setPushEnabled] = useState(false)
  const [pushBusy, setPushBusy] = useState(false)

  useEffect(() => {
    if (!isNative) {
      return
    }
    isPushNotificationsEnabled().then(setPushEnabled)
  }, [isNative])

  const userConsentsForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      marketingConsent: usersProfileMarketing.marketingConsent,
      newsletterConsent: usersProfileMarketing.newsletterConsent
    },
    onSubmit: (values) => {
      usersProfileMarketingUpdate.mutate(values)
    }
  })

  useEffect(() => {
    if (userConsentsForm.dirty) {
      userConsentsForm.submitForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userConsentsForm.dirty])

  const handlePushToggle = async () => {
    if (!accessToken || pushBusy) {
      return
    }
    setPushBusy(true)
    try {
      if (pushEnabled) {
        await unregisterPushNotifications(accessToken)
        setPushEnabled(false)
      } else {
        const granted = await initPushNotifications(accessToken)
        setPushEnabled(granted)
        if (!granted) {
          enqueueSnackbar(
            formatMessage({
              id: 'userDetailsConsents.pushDenied',
              defaultMessage:
                'Powiadomienia są zablokowane. Włącz je w ustawieniach systemowych urządzenia.'
            }),
            { variant: 'warning' }
          )
        }
      }
    } finally {
      setPushBusy(false)
    }
  }

  const content = (
    <>
      {isDesktop && (
        <Typography sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy, mb: '28px' }}>
          {formatMessage({
            id: 'userDetailsConsents.label',
            defaultMessage: 'Zgody i powiadomienia'
          })}
        </Typography>
      )}

      {isNative && (
        <Box sx={{ mb: '24px' }}>
          <SectionLabel>
            {formatMessage({
              id: 'userDetailsConsents.pushSection',
              defaultMessage: 'Powiadomienia push'
            })}
          </SectionLabel>
          <ConsentRow
            first
            title={formatMessage({
              id: 'userDetailsConsents.push',
              defaultMessage: 'Powiadomienia push'
            })}
            subtitle={formatMessage({
              id: 'userDetailsConsents.pushDesc',
              defaultMessage: 'Powiadomienia na tym urządzeniu'
            })}
            checked={pushEnabled}
            disabled={pushBusy}
            onToggle={handlePushToggle}
          />
        </Box>
      )}

      <SectionLabel>
        {formatMessage({
          id: 'userDetailsConsents.consentsSection',
          defaultMessage: 'Zgody'
        })}
      </SectionLabel>
      <Box>
        <ConsentRow
          first
          title={formatMessage({
            id: 'userDetailsConsents.newsletterConsent',
            defaultMessage: 'Notyfikacje z tablicy'
          })}
          checked={userConsentsForm.values.newsletterConsent}
          onToggle={() =>
            userConsentsForm.setFieldValue(
              'newsletterConsent',
              !userConsentsForm.values.newsletterConsent
            )
          }
        />
        <ConsentRow
          title={formatMessage({
            id: 'userDetailsConsents.marketingConsent',
            defaultMessage: 'Zgody marketingowe'
          })}
          checked={userConsentsForm.values.marketingConsent}
          onToggle={() =>
            userConsentsForm.setFieldValue(
              'marketingConsent',
              !userConsentsForm.values.marketingConsent
            )
          }
        />
      </Box>
    </>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'userDetailsConsents.label',
          defaultMessage: 'Zgody i powiadomienia'
        })}
      />
      <BackNavigationContent>
        <Box pt='env(safe-area-inset-top)'>{content}</Box>
      </BackNavigationContent>
    </>
  )
}

export default UserDetailsConsents
